import axios, { AxiosResponse } from "axios";
import { API_URL, APP_VERSION } from "../constants";
import { DIGITAL_HUMAN } from "@common/domain/auth";
import type { FeedbackCategoryId, FeedbackClientContext } from "@common/domain/types";
import { UserInfo } from "../contexts/UserInfoContext";
import {
  GetChatHistoryRes,
  GetSessionRes,
  PostMessageRes,
} from "../models/Chat";
import { resolveAccessToken } from "./Auth";
import { authFetch } from "./authFetch";
import { Persona } from "./Persona";
import { getUserTimeZone } from "../utils/timezone";
import { hydrateExchangeMetadata } from "../helpers/chatMetadataRestore";

export { getUserTimeZone };

export async function postChatMessage(
  message: string,
  chatId: string,
  accessToken: string,
  publicationFromYear: number | null,
  publicationToYear: number | null,
): Promise<PostMessageRes | undefined> {
  const token = await resolveAccessToken(accessToken);
  if (token) {
    const requestBody: {
      question: string;
      chatId: string;
      timeZone?: string;
      publicationFromYear?: number;
      publicationToYear?: number;
      person?: string;
    } = {
      question: message,
      chatId,
      timeZone: getUserTimeZone(),
    };
    if (publicationFromYear !== null) {
      requestBody.publicationFromYear = publicationFromYear;
    }
    if (publicationToYear !== null) {
      requestBody.publicationToYear = publicationToYear;
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    try {
      const response: AxiosResponse = await axios.post(
        `${API_URL}/chat/response`,
        requestBody,
        config
      );
      // check to see if the response indicates an error
      if ("errorType" in response.data) {
        return undefined;
      } else {
        return response.data;
      }
    } catch (error) {
      throw error
    }
  } else return undefined;
}

export interface ChatStreamOptions {
  /** Stable for one user turn and reused if the HTTP request is retried. */
  clientTurnId?: string;
  requirePersistenceAck?: boolean;
  enableWebSearch?: boolean;
  enableWorkflowProgress?: boolean;
  forceWebSearch?: boolean;
  useSynapse?: boolean;
  discoveryMode?: boolean;
  discoveryExtended?: boolean;
  discoveryGuideProfileId?: string;
  discoveryTestRunId?: string;
  synapseWriteMode?: "async" | "sync";
  comparisonRunId?: string;
  comparisonStreamId?: string;
  comparisonStreamLabel?: string;
  parentExchangeId?: string;
  parentChatId?: string;
  comparisonSettings?: Record<string, unknown>;
  chapterSlug?: string;
  requireChapterScope?: boolean;
  learnerInputMode?: "text" | "voice";
  timeZone?: string;
}

export async function postChatMessageStream(
  message: string,
  chatId: string,
  accessToken: string,
  persona?: Persona,
  conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>,
  signal?: AbortSignal,
  streamOptions?: ChatStreamOptions
): Promise<any | undefined> {
  const token = await resolveAccessToken(accessToken);
  if (token) {
    const requestBody: {
      messages: any[];
      chatId: string;
      clientTurnId?: string;
      personaId?: string;
      timeZone?: string;
      requirePersistenceAck?: boolean;
      enableWebSearch?: boolean;
      enableWorkflowProgress?: boolean;
      forceWebSearch?: boolean;
      useSynapse?: boolean;
      discoveryMode?: boolean;
      discoveryExtended?: boolean;
      discoveryGuideProfileId?: string;
      discoveryTestRunId?: string;
      synapseWriteMode?: "async" | "sync";
      comparisonRunId?: string;
      comparisonStreamId?: string;
      comparisonStreamLabel?: string;
      parentExchangeId?: string;
      parentChatId?: string;
      comparisonSettings?: Record<string, unknown>;
      chapterSlug?: string;
      requireChapterScope?: boolean;
      learnerInputMode?: "text" | "voice";
    } = {
      messages: conversationHistory || [],
      chatId,
      personaId: persona?.id,
      timeZone: streamOptions?.timeZone || getUserTimeZone(),
    };

    if (streamOptions?.clientTurnId) {
      requestBody.clientTurnId = streamOptions.clientTurnId;
    }

    if (streamOptions?.requirePersistenceAck === true) {
      requestBody.requirePersistenceAck = true;
    }

    if (typeof streamOptions?.enableWebSearch === 'boolean') {
      requestBody.enableWebSearch = streamOptions.enableWebSearch;
    }

    if (typeof streamOptions?.enableWorkflowProgress === 'boolean') {
      requestBody.enableWorkflowProgress = streamOptions.enableWorkflowProgress;
    }
    if (typeof streamOptions?.forceWebSearch === 'boolean') {
      requestBody.forceWebSearch = streamOptions.forceWebSearch;
    }
    if (typeof streamOptions?.useSynapse === 'boolean') {
      requestBody.useSynapse = streamOptions.useSynapse;
    }
    if (typeof streamOptions?.discoveryMode === 'boolean') {
      requestBody.discoveryMode = streamOptions.discoveryMode;
    }
    if (typeof streamOptions?.discoveryExtended === 'boolean') {
      requestBody.discoveryExtended = streamOptions.discoveryExtended;
    }
    if (streamOptions?.discoveryGuideProfileId) {
      requestBody.discoveryGuideProfileId = streamOptions.discoveryGuideProfileId;
    }
    if (streamOptions?.discoveryTestRunId) {
      requestBody.discoveryTestRunId = streamOptions.discoveryTestRunId;
    }
    if (streamOptions?.synapseWriteMode === 'async' || streamOptions?.synapseWriteMode === 'sync') {
      requestBody.synapseWriteMode = streamOptions.synapseWriteMode;
    }
    if (streamOptions?.comparisonRunId) {
      requestBody.comparisonRunId = streamOptions.comparisonRunId;
    }
    if (streamOptions?.comparisonStreamId) {
      requestBody.comparisonStreamId = streamOptions.comparisonStreamId;
    }
    if (streamOptions?.comparisonStreamLabel) {
      requestBody.comparisonStreamLabel = streamOptions.comparisonStreamLabel;
    }
    if (streamOptions?.parentExchangeId) {
      requestBody.parentExchangeId = streamOptions.parentExchangeId;
    }
    if (streamOptions?.parentChatId) {
      requestBody.parentChatId = streamOptions.parentChatId;
    }
    if (streamOptions?.comparisonSettings) {
      requestBody.comparisonSettings = streamOptions.comparisonSettings;
    }
    if (streamOptions?.chapterSlug) {
      requestBody.chapterSlug = streamOptions.chapterSlug;
    }
    if (streamOptions?.requireChapterScope === true) {
      requestBody.requireChapterScope = true;
    }
    if (streamOptions?.learnerInputMode) {
      requestBody.learnerInputMode = streamOptions.learnerInputMode;
    }

    try {
      const response: Response = await authFetch(
        `${process.env.REACT_APP_STREAMING_API_URL}chat/completions`,
        {
          method: "POST",
          mode: "cors",
          signal,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            ...(streamOptions?.enableWorkflowProgress ? {} : { conversation_id: chatId }),
          },
          body: JSON.stringify(requestBody)
        })
      return response
    } catch (error) {
      throw error
    }
  } else return undefined;
}

export async function getChatSession(
  userInfo: UserInfo | undefined,
  chatId: string,
  userId?: string
): Promise<GetSessionRes | undefined> {
  const token = await resolveAccessToken(userInfo?.accessToken);
  if (token) {
    const headers = {
      Authorization: `Bearer ${token}`,
    };
    try {
      const userParam = userId ? `&userId=${encodeURIComponent(userId)}` : '';
      const response: AxiosResponse = await axios.get(
        `${API_URL}/chat/${encodeURIComponent(chatId)}?chatType=${DIGITAL_HUMAN}${userParam}`,
        {
          headers: headers,
        }
      );

      // Parse metadata to extract safetyRoute and workflow metadata
      if (response.data && response.data.conversation) {
        response.data.conversation = response.data.conversation.map((exchange: any) =>
          hydrateExchangeMetadata(exchange)
        );
      }

      return response.data;
    } catch (error) {
      throw error
    }
  } else return undefined;
}

export async function archiveChatSession(
  chatId: string,
  accessToken: string
): Promise<any> {
  const token = await resolveAccessToken(accessToken);
  if (token) {
    const headers = {
      Authorization: `Bearer ${token}`,
    };
    try {
      const response: AxiosResponse = await axios.delete(
        `${API_URL}/chat/${chatId}?chatType=${DIGITAL_HUMAN}`,
        {
          headers: headers,
        }
      );

      return response.data;
    } catch (error) {
      throw error
    }
  } else return undefined;
}

/**
 * Captured on every rating so delivery complaints ("too slow", "looked broken")
 * carry the context needed to reproduce them. Built here rather than at each
 * call site so both surfaces get it without touching their handlers.
 */
function buildClientContext(): FeedbackClientContext | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const width = window.innerWidth;
    const deviceType: FeedbackClientContext['deviceType'] =
      width < 768 ? 'mobile' : width < 1024 ? 'tablet' : 'desktop';
    return {
      userAgent: navigator.userAgent,
      deviceType,
      viewport: `${width}x${window.innerHeight}`,
      appVersion: APP_VERSION || undefined,
      hostname: window.location?.hostname || undefined,
    };
  } catch {
    return undefined;
  }
}

export async function putResponseRating(
  chatId: string,
  exchangeId: string,
  rating: number | null,
  comment?: string,
  accessToken?: string,
  personaId?: string,
  categories?: FeedbackCategoryId[]
): Promise<any> {
  const token = await resolveAccessToken(accessToken);
  if (!token) {
    throw new Error("Session expired");
  }

  const headers = {
    Authorization: `Bearer ${token}`,
  };

  const request = { rating, comment, personaId, categories, clientContext: buildClientContext() };

  try {
    const res = await axios.put(
      `${API_URL}/chat/${chatId}/exchange/${exchangeId}/rating`,
      request,
      { headers }
    );
    return res.data;
  } catch (err: any) {
    const message =
      err?.response?.data?.message ??
      err?.response?.data ??
      err?.message ??
      "Failed to submit rating";

    throw new Error(typeof message === "string" ? message : JSON.stringify(message));
  }
}


export async function getChatHistory(
  userInfo: UserInfo | undefined,
  personaId?: string
): Promise<GetChatHistoryRes | undefined> {
  const token = await resolveAccessToken(userInfo?.accessToken);
  if (token) {
    const headers = {
      Authorization: `Bearer ${token}`,
    };
    try {
      const personaQuery = personaId ? `&persona_id=${personaId}` : '';
      const response = await axios.get(`${API_URL}/chats?chatType=${DIGITAL_HUMAN}${personaQuery}`, {
        headers: headers,
      });

      return response.data;
    } catch (error) {
      throw error
    }
  } else return undefined;
}
